import { Component, ChangeDetectionStrategy, signal, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CalculationStorageService } from '../../services/calculation-storage.service';

@Component({
  selector: 'app-duplicate-calculation-dialog',
  imports: [CommonModule, FormsModule],
  template: `
    @if (isOpen()) {
      <div class="dialog-overlay" (click)="close()">
        <div class="dialog-content" (click)="$event.stopPropagation()">
          <div class="dialog-header">
            <h2>Berechnung duplizieren</h2>
            <button class="close-btn" (click)="close()" aria-label="Schließen">&times;</button>
          </div>
          
          <div class="dialog-body">
            <div class="form-group">
              <label for="calc-name">Name der Kopie *</label>
              <input 
                id="calc-name"
                type="text" 
                [value]="nameInput()" 
                (input)="updateName($event)"
                (blur)="validateName()"
                [class.error]="nameError()"
              />
              @if (nameError()) {
                <span class="error-message">{{ nameError() }}</span>
              }
            </div>
            
            <p class="hint">
              <strong>Hinweis:</strong> Es wird eine exakte Kopie der Berechnung "{{ originalName() }}" mit allen Daten erstellt.
            </p>
          </div>
          
          <div class="dialog-footer">
            <button class="btn btn-secondary" (click)="close()">Abbrechen</button>
            <button 
              class="btn btn-primary" 
              (click)="duplicate()"
              [disabled]="!isValid() || isDuplicating()"
            >
              {{ isDuplicating() ? 'Dupliziere...' : 'Duplizieren' }}
            </button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .dialog-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }
    
    .dialog-content {
      background: white;
      border-radius: 8px;
      max-width: 500px;
      width: 90%;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }
    
    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.5rem;
      border-bottom: 1px solid #e5e7eb;
    }
    
    .dialog-header h2 {
      margin: 0;
      font-size: 1.5rem;
      color: #1f2937;
    }
    
    .close-btn {
      background: none;
      border: none;
      font-size: 2rem;
      color: #6b7280;
      cursor: pointer;
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background-color 0.2s;
    }
    
    .close-btn:hover {
      background-color: #f3f4f6;
      color: #1f2937;
    }
    
    .dialog-body {
      padding: 1.5rem;
    }
    
    .form-group {
      margin-bottom: 1rem;
    }
    
    .form-group label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 600;
      color: #374151;
    }
    
    .form-group input {
      width: 100%;
      padding: 0.75rem;
      border: 2px solid #d1d5db;
      border-radius: 6px;
      font-size: 1rem;
      transition: border-color 0.2s;
    }
    
    .form-group input:focus {
      outline: none;
      border-color: var(--bright-blue, #3b82f6);
    }
    
    .form-group input.error {
      border-color: #ef4444;
    }
    
    .error-message {
      display: block;
      color: #ef4444;
      font-size: 0.875rem;
      margin-top: 0.25rem;
    }
    
    .hint {
      background: #eff6ff;
      border-left: 3px solid #3b82f6;
      padding: 0.75rem;
      border-radius: 4px;
      font-size: 0.875rem;
      color: #1e40af;
      margin: 1rem 0 0 0;
    }
    
    .dialog-footer {
      display: flex;
      gap: 0.75rem;
      justify-content: flex-end;
      padding: 1.5rem;
      border-top: 1px solid #e5e7eb;
    }
    
    .btn {
      padding: 0.75rem 1.5rem;
      border-radius: 6px;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      border: none;
    }
    
    .btn-secondary {
      background: #f3f4f6;
      color: #374151;
    }
    
    .btn-secondary:hover {
      background: #e5e7eb;
    }
    
    .btn-primary {
      background: var(--bright-blue, #3b82f6);
      color: white;
    }
    
    .btn-primary:hover:not(:disabled) {
      background: #2563eb;
    }
    
    .btn-primary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DuplicateCalculationDialogComponent {
  private storageService = inject(CalculationStorageService);
  
  isOpen = signal(false);
  nameInput = signal('');
  nameError = signal('');
  isDuplicating = signal(false);
  
  calculationId = signal<number | null>(null);
  originalName = signal('');
  existingNames = signal<string[]>([]);
  
  onDuplicated = signal<((id: number) => void) | null>(null);
  
  isValid = computed(() => {
    return this.nameInput().trim().length > 0 && !this.nameError();
  });
  
  /**
   * Dialog öffnen
   */
  open(id: number, originalName: string, allCalculationNames: string[]) {
    this.calculationId.set(id);
    this.originalName.set(originalName);
    this.existingNames.set(allCalculationNames);
    
    // Generiere Namensvorschlag mit Suffix
    const suggestedName = this.storageService.generateDuplicateNameSuggestion(
      allCalculationNames, 
      originalName
    );
    
    this.nameInput.set(suggestedName);
    this.nameError.set('');
    this.isOpen.set(true);
  }
  
  /**
   * Dialog schließen
   */
  close() {
    this.isOpen.set(false);
    this.calculationId.set(null);
    this.originalName.set('');
    this.nameInput.set('');
    this.nameError.set('');
    this.existingNames.set([]);
  }
  
  /**
   * Name aktualisieren
   */
  updateName(event: Event) {
    const input = event.target as HTMLInputElement;
    this.nameInput.set(input.value);
    if (this.nameError()) {
      this.validateName();
    }
  }
  
  /**
   * Name validieren
   */
  validateName() {
    const name = this.nameInput().trim();
    
    if (!name) {
      this.nameError.set('Name ist erforderlich');
      return;
    }
    
    if (name.length > 255) {
      this.nameError.set('Name darf maximal 255 Zeichen lang sein');
      return;
    }
    
    // Check if name already exists
    this.storageService.checkNameExists(name).subscribe({
      next: (result) => {
        if (result.exists) {
          this.nameError.set('Eine Berechnung mit diesem Namen existiert bereits');
        } else {
          this.nameError.set('');
        }
      },
      error: () => {
        this.nameError.set('');
      }
    });
  }
  
  /**
   * Duplizieren
   */
  duplicate() {
    if (!this.isValid() || this.isDuplicating()) {
      return;
    }
    
    const id = this.calculationId();
    if (!id) {
      return;
    }
    
    this.isDuplicating.set(true);
    
    this.storageService.duplicateCalculation(id, this.nameInput().trim()).subscribe({
      next: (result) => {
        this.isDuplicating.set(false);
        const callback = this.onDuplicated();
        if (callback) {
          callback(result.calculationId);
        }
        this.close();
      },
      error: (error) => {
        this.isDuplicating.set(false);
        this.nameError.set(error?.error?.message || 'Fehler beim Duplizieren');
      }
    });
  }
}
